import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment-timezone';
import LoadQueueHeader from './LoadQueueHeader';
import LoadQueueList from './LoadQueueList';
import {
	getLoadQueueList, setSelectedLoadIds, getLoadRegionList, getPlanterEGList, setIsComplete
} from '../../../actions/load';
import { Unit } from '../../../helpers/unit';
import LoadQueueBulkEdit from './LoadQueueBulkEdit';
import LoadQueueFilterSection from './LoadQueueFilterSection';
import { getUserConfiguration } from '../../../actions/user';
import { setLoadQueueSearchText, completedLoad, approveLoad } from '../../../actions/load';
import { isPlannedOrTodate, isToday, usePermission } from '../../../helpers/common';
import { useLoadQueueContext } from './context/LoadQueueContext';
import { API_DATETIME_FORMAT } from '../../../constants';
import { getFarmSettings } from '../../../actions/farm';
import { getSeedLot, getSeedSupplier, getSeedVariety, getSeedType, getSeedCuttingGroup } from '../../../actions/plan';
import { getPlantingQueOptionSession, setPlantingQueOptionSession, getUserId } from '../../../helpers/session';


const LoadQueue = props => {

	const { loadQueueSearchText, selectedLoadIds } = useSelector(state => state.load);
	const loadList = useSelector(state => state.load.loadQueueList.data);
	const timezone = useSelector(state => state.farm.currentFarm.timezone_identifier);
	const { range, rangeId, custom } = useSelector(state => state.analyze.searchParams);
	const autoRefresh = useSelector(state => state.user.configuration.autoRefresh
		? state.user.configuration.autoRefresh.load_queue : true);
	const roleId = useSelector(state => state.auth.userInfo.role_id);
	const farmSettings = useSelector(state => state.farm.farmSettings.farm_config);

	const dispatch = useDispatch();
	const allow = usePermission();
	const unit = Unit.getInstance();
	const { farmId } = props;

	const planTypeId = useSelector(state => state.plan.selectedPlanType);
	const loadQueueType = useSelector(state => state.load.loadQueueType);
	const fieldRegionList = useSelector(state => state.load.regionList.data);
	const plantingQueueInputs = useSelector(state => state.load.plantingQueueInput)
	const sourceEgList = useSelector(state => state.load.sourceEGList.data);
	const farmConfig = useSelector(state => state.farm.farmSettings.farm_config);
	const isNeedPlanterValues = useSelector(state => state.load.isNeedPlanterValues);
	const currentDate = moment().utc().format();
	const [searchText, setSearchText] = useState(loadQueueSearchText);
	const [loadMoreCount, setLoadMoreCount] = useState(1);

	const { isEdited, setIsEdited, setApproving, editedLoadsById, approving } = useLoadQueueContext();
	const isAllowMap = allow("map", "view");
	const [isApproveDataValid, setApproveDataValid] = useState(true);

	let timerId = null;

	const { load_queue: { visible: dropDownVisible, configuration: { load_queue_params = [] } = {} } = {} } = farmSettings || {};

	const { is_planter_allocation = {} } = farmSettings || {};
	const isPlanterAllocation = (is_planter_allocation && is_planter_allocation.visible);

	const [formDatas, setFormDatas] = useState(null);

	const recentHours = useMemo(() => {
		let updatedHours;
		if (farmConfig) {
			updatedHours = farmConfig && farmConfig.load_queue_recent_hours &&
				// farmConfig.load_queue_recent_hours.visible &&
				farmConfig.load_queue_recent_hours.recent_hours ?
				farmConfig.load_queue_recent_hours.recent_hours : 0;
			return updatedHours;
		}
	}, [farmConfig]);

	useEffect(() => {
		if (dropDownVisible && load_queue_params && load_queue_params.length > 0) {
			const tmp_fields = load_queue_params.map((e) => {
				return {
					...e,
					options: (Array.isArray(e.values) && e.values.length > 0)
						? e.values.map((elm) => {
							return { value: elm, label: elm }
						}) : []
				}
			})
			setFormDatas(tmp_fields);
		} else {
			setFormDatas(null);
		}
	}, [dropDownVisible, load_queue_params])
	useEffect(() => {
		dispatch(getUserConfiguration("autoRefresh", { configuration_name: "AutoRefresh" }));
		setSearchText(loadQueueSearchText);
	}, []);

	useEffect(() => {
		dispatch(getLoadRegionList({ farm_uuid: farmId }))
		dispatch(getPlanterEGList({ farmId, selectedPlanType: planTypeId }))
		dispatch(getFarmSettings({ farm_uuid: farmId, plan_type_id: planTypeId }));
	}, [farmId]);

	useEffect(() => {
		clearTimer();
		if (loadQueueType === 1 && autoRefresh && selectedLoadIds.length === 0 && !isEdited && isToday(range)) {
			timerId = setInterval(getLoadList, 2 * 60 * 1000);
		}
		return clearTimer;
	}, [farmId, loadQueueType, rangeId, isEdited, selectedLoadIds, autoRefresh]);

	useEffect(() => {
		getLoadList();
		dispatch(getSeedVariety(farmId));
		dispatch(getSeedSupplier(farmId));
		dispatch(getSeedLot(farmId));
		dispatch(getSeedType(farmId));
		dispatch(getSeedCuttingGroup(farmId));
	}, [farmId, loadQueueType, rangeId, planTypeId, recentHours]);

	useEffect(() => {
		if (loadList.length === 0) setLoadMoreCount(1);
	}, [loadList]);

	const handleRefresh = () => {
		getLoadList();
		setIsEdited(false);
		dispatch(setSelectedLoadIds([]));
	};

	const getLoadList = () => {
		let from = null, to = null;
		if (!isPlannedOrTodate(range)) {
			if (custom.from && custom.to) {
				from = isToday(range) ? moment.utc(custom.from).subtract(recentHours, "hours").format(API_DATETIME_FORMAT) :
					moment.utc(custom.from).format(API_DATETIME_FORMAT);
				to = moment.utc(custom.to).format(API_DATETIME_FORMAT);

			} else {
				const now = moment.utc().tz(timezone);
				from = now.clone().startOf("day").format(API_DATETIME_FORMAT);
				to = now.clone().endOf("day").format(API_DATETIME_FORMAT);
			}
		}

		let loadParams = {
			farmId,
			rangeType: range,
			...!isPlannedOrTodate(range) && { fromDate: from, toDate: to },
			is_current_customer_load: [9].includes(roleId),
			planTypeId,
		};
		loadParams[isPlanterAllocation ? "completed" : "approved"] = (loadQueueType === 2);

		dispatch(getLoadQueueList(loadParams));
	};

	const clearTimer = () => {
		if (timerId) clearInterval(timerId);
		timerId = null;
	};

	const handleMultiApprove = () => {
		setApproving(true);
		dispatch(setIsComplete(true));
		const seletedLoads = loadList.filter(item => selectedLoadIds.indexOf(item.loadActivityId) >= 0);
		let approvalLoads = seletedLoads.map(item => ({ ...item, ...editedLoadsById[item.loadActivityId] }));
		const data = [];
		let isAllplantingQueueValuesNotPresent = false;
		for (let input in plantingQueueInputs) {
			if (plantingQueueInputs[input] === null || plantingQueueInputs[input] === 0 || plantingQueueInputs[input] === '') {
				isAllplantingQueueValuesNotPresent = true;
				break;
			}
		}
		if (isNeedPlanterValues && !isAllplantingQueueValuesNotPresent) {
			approvalLoads = approvalLoads.map(load => ({
				...load, loadSequence: [
					{
						sequence: 1,
						sequence_id: Date.now(),
						load_activity_uuid: load.loadActivityId,
						source_equipment_group_uuid: load.targetEquipmentGroupId,
						target_equipment_group_uuid: plantingQueueInputs.planterId,
						load_capacity: plantingQueueInputs.loadCapacity ? unit.fromWeight(plantingQueueInputs.loadCapacity, 2) : 0,
						geo_region_uuid: plantingQueueInputs.fieldRegionId,
						geo_shape_uuid: plantingQueueInputs.fieldShapeId,
						type: 1,
						status_id: 1,
						latitude: null,
						longitude: null,
						created_date: currentDate,
						created_by: getUserId(),
						modified_date: null,
						modified_by: null
					}
				]
			}))
		}

		const { fieldShapeId, planterId } = plantingQueueInputs;
		const findShape = fieldShapeId ? fieldRegionList.filter(e => e.geo_shape_uuid === fieldShapeId) : [];

		approvalLoads.forEach(item => {

			//const field_geo_shape_uuid = fieldShapeId ? fieldShapeId : (item && item.fieldShapeId) ? item.fieldShapeId : null;
			const offload_geo_region_name = (findShape.length > 0) ? findShape[0].geo_region_name : (item && item.offloadGeoRegionName) ? item.offloadGeoRegionName : null;
			const offload_geo_region_uuid = (findShape.length > 0) ? findShape[0].geo_region_uuid : (item && item.offloadGeoRegionId) ? item.offloadGeoRegionId : null;
			const offload_geo_shape_name = (findShape.length > 0) ? findShape[0].geo_shape_name : (item && item.offloadGeoShapeName) ? item.offloadGeoShapeName : null;
			const offload_geo_storage_location_uuid = planterId ? planterId : (item && item.planterId) ? item.planterId : null;;


			let params = {
				farm_uuid: farmId,
				source_equipment_group_uuid: item.sourceEquipmentGroupId,
				target_equipment_group_uuid: item.targetEquipmentGroupId,
				load_activity_uuid: item.loadActivityId,
				load_capacity: item.loadCapacity,
				light_weight: item.lightWeight ? item.lightWeight : null,
				tare_weight: item.tareWeight ? Number(item.tareWeight) : null,
				load_gross_weight: item.loadGrossWeight ? Number(item.loadGrossWeight) : null,
				load_params: item.load_params ? item.load_params : null,
				load_sequence: (item.loadSequence && item.loadSequence.length > 0) ? item.loadSequence : []
			};

			if (isPlanterAllocation) {
				params = {
					...params,
					//field_geo_shape_uuid,
					offload_geo_region_name,
					offload_geo_region_uuid,
					offload_geo_shape_name,
					offload_geo_storage_location_uuid,
				}
			}
			data.push(params);
		});
		const isValid = checkValidation(data);
		if (!isValid) {
			setApproving(false);
			return;
		};
		if (isPlanterAllocation) {
			dispatch(completedLoad({ farm_uuid: farmId, loads: data, planTypeId }, (isApproved) => {
				if (isApproved) {
					dispatch(setIsComplete(false));
					dispatch(setSelectedLoadIds([]));
				}

			}));
		} else {
			dispatch(approveLoad({ farm_uuid: farmId, loads: data, planTypeId }, (isApproved) => {
				if (isApproved) {
					dispatch(setSelectedLoadIds([]));
					setIsEdited(false);
				}
				setApproving(false);
			}));
		}
		return
	};

	const checkValidation = (data) => {
		let isValid = true;
		if (formDatas && formDatas.length > 0) {
			for (let i = 0; i < formDatas.length; i++) {
				const formData = formDatas[i];
				if (!formData) continue;
				if (formData.required) {
					const filterList = data.filter((el) => (!el.load_params || (el.load_params && !el.load_params[formData.key])));
					if (filterList && filterList.length > 0) {
						isValid = false;
						setApproveDataValid(false);
						break;
					}
				}
				if (formData.hasOwnProperty('rules') && formData.rules.hasOwnProperty('max_length')) {
					const filterList = data.filter((el) =>
					(el.load_params && el.load_params[formData.key] && el.load_params[formData.key].toString().trim().length
						> formData.rules.max_length));
					if (filterList && filterList.length > 0) {
						isValid = false;
						setApproveDataValid(false);
						break;
					}
				}
			}
		}
		return isValid;
	}


	const { fieldRegionOptions } = useMemo(() => {
		const fieldReginListName = fieldRegionList.filter(e => e.geo_region_type_id === 2 && e.active);
		const fieldRegionNameOptions = fieldReginListName.map(e => ({
			value: e.geo_shape_uuid,
			label: e.geo_shape_name,
			regionId: e.geo_region_uuid
		}));

		const sessionFilterOptions = getPlantingQueOptionSession();
		const fieldRegionSessionOptions = (sessionFilterOptions && sessionFilterOptions.fieldRegionSessionOptions)
			? sessionFilterOptions.fieldRegionSessionOptions : [];

		const orderedArray = fieldRegionNameOptions.filter((e) => fieldRegionSessionOptions.includes(e.value)).sort((a, b) => {
			const aIndex = fieldRegionSessionOptions.indexOf(a.value);
			const bIndex = fieldRegionSessionOptions.indexOf(b.value);
			return aIndex - bIndex;
		});

		const sortedFieldRegions = [...orderedArray, ...fieldRegionNameOptions.filter((e) => !fieldRegionSessionOptions.includes(e.value))];
		return { fieldRegionOptions: sortedFieldRegions, fieldReginListName };
	}, [fieldRegionList]);

	const { sourceEGOptions } = useMemo(() => {
		const sourceEGListName = sourceEgList.filter(e => e.plan_type_id === 2 && e.primary_category_id === 4);
		const sourceEGNameOptions = sourceEGListName.map(e => ({
			value: e.equipment_group_uuid,
			label: e.equipment_group_name,
		}));

		const sessionFilterOptions = getPlantingQueOptionSession();
		const sourceEGSessionOptions = (sessionFilterOptions && sessionFilterOptions.sourceEGSessionOptions)
			? sessionFilterOptions.sourceEGSessionOptions : [];

		const orderedArray = sourceEGNameOptions.filter((e) => sourceEGSessionOptions.includes(e.value)).sort((a, b) => {
			const aIndex = sourceEGSessionOptions.indexOf(a.value);
			const bIndex = sourceEGSessionOptions.indexOf(b.value);
			return aIndex - bIndex;
		});

		const sortedsourceEG = [...orderedArray, ...sourceEGNameOptions.filter((e) => !sourceEGSessionOptions.includes(e.value))];
		return { sourceEGOptions: sortedsourceEG, sourceEGListName };
	}, [sourceEgList]);

	const setSessionOptions = (key, value) => {
		const optionsObj = getPlantingQueOptionSession();
		let newOptionsObj = {};
		if (optionsObj && optionsObj.hasOwnProperty(key)) {
			let oldOptions = optionsObj[key];
			if (oldOptions && oldOptions.length > 0) {
				oldOptions.unshift(value);
				oldOptions = [...new Set([...oldOptions])];
			}
			newOptionsObj = {
				...optionsObj,
				[key]: oldOptions
			}
		} else {
			newOptionsObj = {
				...optionsObj,
				[key]: [value]
			}
		}
		setPlantingQueOptionSession(newOptionsObj);
	}

	return <div className={`load-queue animate animate__slideInRight ${!isAllowMap && [9].includes(roleId) && 'customer-load-queue'}`}>
		<LoadQueueHeader
			farmId={farmId}
			onHide={props.onHide}
			onRefresh={handleRefresh}
			onSearch={(text) => {
				setSearchText(text);
				dispatch(setLoadQueueSearchText(text));
			}}
			searchText={searchText}
			isEdited={isEdited}
			selectedLoadIds={selectedLoadIds}
			isPlanterAllocation={isPlanterAllocation}
		/>
		<div className="m-0">
			<LoadQueueFilterSection
				onSearch={(text) => {
					setSearchText(text);
					dispatch(setLoadQueueSearchText(text));
				}}
				searchText={searchText}
				handleMultiApprove={handleMultiApprove}
				isApproving={approving}
				selectedLoadIds={selectedLoadIds}
				isPlanterAllocation={isPlanterAllocation}
			/>

			{(loadQueueType === 1 && loadList.length > 0) && (
				<Row className='w-100 m-0'>
					<Col className='col-12 pr-0'>
						<LoadQueueBulkEdit fieldRegionOptions={fieldRegionOptions} sourceEGOptions={sourceEGOptions} plantingQueueInputs={plantingQueueInputs}
							setSessionOptions={setSessionOptions} isPlanterAllocation={isPlanterAllocation} />
					</Col>
				</Row>)}
		</div>
		<div className="load-queue-body">
			<LoadQueueList
				farmId={farmId}
				searchText={searchText}
				loadMoreCount={loadMoreCount}
				dropDownVisible
				formDatas={formDatas}
				isApproveDataValid={isApproveDataValid}
				setLoadMoreCount={setLoadMoreCount}
				fieldRegionOptions={fieldRegionOptions}
				sourceEGOptions={sourceEGOptions}
				isPlanterAllocation={isPlanterAllocation} />
		</div>
	</div>;
};

export default LoadQueue;